import React from 'react';
import { Page, Document, StyleSheet, Text, View } from '@react-pdf/renderer';
import PdfHeader from './components/reusable/PdfHeader';
import PageNumber from './components/reusable/PageNumber';
import ServiceTechnicianSection from './components/reusable/ServiceTechnicianSection';
import ServicePartsSection from './components/reusable/ServicePartsSection';

const styles = StyleSheet.create({
  body: {
    padding: 15,
    paddingBottom: 25,
  },
  section: {
    marginTop: 8,
    padding: 6,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: '#cccccc',
    borderRadius: 3,
    backgroundColor: '#f8f9fa',
  },
  sectionTitle: {
    fontSize: 11,
    fontFamily: 'Helvetica-Bold',
    marginBottom: 6,
    paddingBottom: 3,
    borderBottomWidth: 1,
    borderBottomStyle: 'solid',
    borderColor: '#dee2e6',
    color: '#495057',
    textAlign: 'center',
  },
  twoColRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 6,
  },
  col: {
    width: '49%'
  },
  checkboxRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 3,
  },
  checkbox: {
    width: 8,
    height: 8,
    borderWidth: 0.5,
    borderStyle: 'solid',
    borderColor: '#000000',
    marginRight: 4,
    textAlign: 'center',
    fontSize: 6,
  },
  label: {
    fontSize: 8,
    fontFamily: 'Helvetica',
    color: '#212529',
    flex: 1,
  },
  subTitle: {
    fontSize: 10,
    fontFamily: 'Helvetica-Bold',
    color: '#495057',
    marginBottom: 4,
    borderBottomWidth: 0.5,
    borderBottomStyle: 'solid',
    borderColor: '#dee2e6',
    paddingBottom: 1,
  },
  ampsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 4,
  },
  ampBox: {
    minWidth: 30,
    paddingHorizontal: 2,
    paddingVertical: 2,
    borderBottomWidth: 0.5,
    borderBottomStyle: 'solid',
    borderColor: '#cccccc',
    backgroundColor: '#f8f9fa',
    fontSize: 8,
    fontFamily: 'Helvetica',
  },
  notesText: {
    fontSize: 9,
    fontFamily: 'Helvetica',
    lineHeight: 1.3,
  },
});

interface PreSeasonPdfProps {
  data: {
    baseSheet: any;
    preSeason: any;
    parts: any[];
    techs: any[];
  };
  user: any;
  showFullDetails?: boolean;
}

const CheckRow = ({ label, checked }: { label: string; checked: boolean }) => (
  <View style={styles.checkboxRow}>
    <View style={styles.checkbox}>
      <Text style={{ backgroundColor: '#000000', color: '#ffffff' }}>{checked ? 'X' : ''}</Text>
    </View>
    <Text style={styles.label}>{label}</Text>
  </View>
);

const renderAmpValues = (values: (string | number | undefined)[]) => {
  const clean = values.filter(v => v !== undefined && v !== null && `${v}`.trim() !== '');
  if (clean.length === 0) return null;
  return (
    <View style={styles.ampsRow}>
      {clean.map((v, idx) => (
        <Text key={idx} style={styles.ampBox}>{`${v}`}</Text>
      ))}
    </View>
  );
};

const PreSeasonPdf: React.FC<PreSeasonPdfProps> = ({ data, user, showFullDetails = true }) => {
  const { baseSheet, preSeason, parts, techs } = data;
  const sheetForHeader = {
    ...baseSheet,
    id: baseSheet?.id || baseSheet?.sheetId || data?.preSeason?.id,
  };

  return (
    <Document>
      <Page size="A4" style={styles.body}>
        <PdfHeader company={baseSheet.company} sheet={sheetForHeader} />

        {/* Fresh Air Doors & Louvers */}
        <View style={styles.twoColRow}>
          <View style={[styles.section, styles.col]}>
            <Text style={styles.sectionTitle}>Fresh Air Doors</Text>
            <CheckRow label="Lubricate actuator(s), run door(s) open and closed - check operation." checked={!!preSeason?.fadlube} />
            <CheckRow label="Check settings and make necessary calibration and adjustments." checked={!!preSeason?.fadsetting} />
          </View>
          <View style={[styles.section, styles.col]}>
            <Text style={styles.sectionTitle}>Louvers</Text>
            <CheckRow label="Lubricate linkage and ball joints; run louver(s) and check mod motors." checked={!!preSeason?.louverlube} />
            <CheckRow label="Check settings and make necessary adjustments." checked={!!preSeason?.louversetting} />
            <CheckRow label="Check exhaust louver(s)." checked={!!preSeason?.louverexhaust} />
          </View>
        </View>

        {/* Fans & ClimaCell */}
        <View style={styles.twoColRow}>
          <View style={[styles.section, styles.col]}>
            <Text style={styles.sectionTitle}>Fans</Text>
            <CheckRow label="Inspect fan propellers and frames for damage." checked={!!preSeason?.faninspect} />
            <CheckRow label="Run motor(s) and check for unusual bearing noise." checked={!!preSeason?.fanmotor} />
            <CheckRow label="Check motor amperage." checked={!!preSeason?.fanamp} />
            {renderAmpValues([
              preSeason?.fanamp1, preSeason?.fanamp2, preSeason?.fanamp3, preSeason?.fanamp4,
              preSeason?.fanamp5, preSeason?.fanamp6, preSeason?.fanamp7, preSeason?.fanamp8,
            ])}
            <CheckRow label="Check operation of Frequency Drive(s)." checked={!!preSeason?.fanfreq} />
          </View>
          <View style={[styles.section, styles.col]}>
            <Text style={styles.sectionTitle}>ClimaCell</Text>
            <CheckRow label="Clean tank, check float valve, fill with fresh water." checked={!!preSeason?.climaclean} />
            <CheckRow label="Check pump operation; clean screens." checked={!!preSeason?.climapump} />
            <CheckRow label="Clean distribution pipe." checked={!!preSeason?.climapipe} />
            <CheckRow label="Clean aspirator cabinet and check wick (if applicable)." checked={!!preSeason?.climawick} />
            <CheckRow label="Check pump amperage." checked={!!preSeason?.climaamp} />
            {renderAmpValues([preSeason?.climaamp1, preSeason?.climaamp2])}
          </View>
        </View>

        {/* Humidifiers & Control Panel */}
        <View style={styles.twoColRow}>
          <View style={[styles.section, styles.col]}>
            <Text style={styles.sectionTitle}>Humidifiers</Text>
            <CheckRow label="Clean tubs, check float valve, fill with fresh water." checked={!!preSeason?.humclean} />
            <CheckRow label="Run motor(s) and check for unusual bearing noise." checked={!!preSeason?.hummotor} />
            <CheckRow label="Check motor amperage." checked={!!preSeason?.humamp} />
            {renderAmpValues([preSeason?.humamp1, preSeason?.humamp2, preSeason?.humamp3, preSeason?.humamp4, preSeason?.humamp5])}
          </View>
          <View style={[styles.section, styles.col]}>
            <Text style={styles.sectionTitle}>Control Panel</Text>
            <CheckRow label="Check modes of operation for proper function." checked={!!preSeason?.panelmode} />
            <CheckRow label="Check calibration (OA control, plenum sensor, low limit, humidity sensor)." checked={!!preSeason?.panelcal} />
            <CheckRow label="Check light bulbs in light tree." checked={!!preSeason?.panellight} />
            <CheckRow label="Clean interior and exterior of panel." checked={!!preSeason?.panelclean} />
            <CheckRow label="Set plenum temp at 55°. Control panel OK to run." checked={!!preSeason?.panelset} />
            <CheckRow label="Check UPS (Uninterruptible Power Supply)." checked={!!preSeason?.ups} />
          </View>
        </View>

        {/* Notes */}
        {(typeof preSeason?.notes === 'string' && preSeason.notes.trim() !== '') ? (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Notes</Text>
            <Text style={styles.notesText}>{preSeason.notes}</Text>
          </View>
        ) : null}

        {/* Technicians */}
        {techs && techs.length > 0 && (
          <ServiceTechnicianSection techs={techs} />
        )}

        {/* Parts */}
        {parts && parts.length > 0 && (
          <ServicePartsSection parts={parts} />
        )}

        <PageNumber stage={preSeason.stage} sheetType="Pre-Season" />
      </Page>
    </Document>
  );
};

export default PreSeasonPdf;
